import React, { useEffect, useState } from "react";
import { Box, Stack, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "@apollo/client";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import { GET_CLOSE_COLORS, tokenOptions } from "../../../backend/graphql";
import { useAppSelector, useHistoryState } from "../../../app/hooks";
import { backendURL } from "../../../constants";
import { formatPrice } from "../../../tools";
import InfoRow from "../../widgets/InfoRow";
import PageTitle from "../../widgets/PageTitle";
import PaginatedGallery from "../../widgets/PaginatedGallery";
import SectionPaper from "../../widgets/SectionPaper";
import SectionTitle from "../../widgets/SectionTitle";
import { galleryLayout, GALLERY_CHROME } from "../../widgets/galleryLayout";
import StoneFilterBar from "./StoneFilterBar";
import { DEFAULT_FILTERS, filterExpression, StoneFilters } from "./filters";
import { DiamondOptions } from "./useDiamondOptions";

// Which picture a result tile shows.
export type Picture = "original" | "weblike";

// The color the search ranks by, and the color systems it narrows by. The OKLCh color is always
// given: it is what orders the answer. The four systems are each optional and combine freely.
export type ColorSearch = {
  okL: number;
  okC: number;
  okH: number;
  // The color name split into the three fields it is built from, each optional.
  fancyIntensity?: string | null;
  fancySaturation?: string | null;
  fancyOvertone?: string | null;
  fancyColor?: string | null;
  langermanColor?: string | null;
  flat?: number | null;
  melee?: number | null;
};

type StoneSearchProps = {
  search: ColorSearch;
  // The stone the search was started from, kept out of its own results.
  excludeDiamondID?: string;
  options: DiamondOptions | undefined;
  // The color reference of the page — a picker on one screen, a stone on the other. It is given the
  // picture the gallery is showing, so a screen that displays the source stone follows the tabs.
  colorArea: (picture: Picture) => React.ReactNode;
  // A screen whose color systems are switches rather than choices puts them here: they are filters
  // like the others, and share their section, on their own line above them.
  colorFilters?: React.ReactNode;
  // How much room that area needs, so the gallery below fills exactly the rest of the viewport.
  headerHeight: number;
};

// The match is always computed from the stone's diamond analysis; the tabs only choose what is
// displayed. A stone photographed only on its pattern is shown by the pattern among the originals;
// the web-likes show only the stones that have one — a box or a set gets none.
function pictureID(dist: any, picture: Picture) {
  if (picture === "original") {
    return dist.originalImageID ?? dist.patternImageID;
  }
  return dist.webLikeImageID;
}

// A match's tooltip: the same Characteristics/Pricing layout as the estimator's similar diamonds,
// limited to the fields the close-colors query returns.
function DiamondTooltip({ diamond }: { diamond: any }) {
  if (!diamond) {
    return <>Unknown</>;
  }
  return (
    <Stack direction="column" spacing={1}>
      <Box>
        <Typography variant="overline">Characteristics</Typography>
        <Stack>
          <InfoRow label="Source" value={diamond.source} />
          <InfoRow label="SKU" value={diamond.sKU} />
          <InfoRow label="GIA Color" value={diamond.color} />
          <InfoRow label="Clarity" value={diamond.clarity} />
          <InfoRow label="Carat" value={diamond.carat} />
          <InfoRow label="Cut" value={diamond.cut} />
          <InfoRow label="Shape" value={diamond.shape} />
        </Stack>
      </Box>
      <Box>
        <Typography variant="overline">Pricing</Typography>
        <Stack>
          <InfoRow
            label="Price"
            value={diamond.price != null ? formatPrice(diamond.price) : null}
          />
        </Stack>
      </Box>
    </Stack>
  );
}

function StoneGallery({
  computedWidth,
  computedHeight,
  search,
  excludeDiamondID,
  options,
  colorArea,
  colorFilters,
  headerHeight,
}: StoneSearchProps & { computedWidth: number; computedHeight: number }) {
  const token = useAppSelector((state) => state.auth.token);

  const [currentPage, setCurrentPage] = useHistoryState("currentPage", 1);
  const [filters, setFilters] = useHistoryState<StoneFilters>(
    "filters",
    DEFAULT_FILTERS,
  );
  const [picture, setPicture] = useHistoryState<Picture>("picture", "original");
  const [displayedData, setDisplayedData] = useState<any>(null);

  const { cols, rows, perPage } = galleryLayout(
    computedWidth,
    138,
    138,
    headerHeight,
    computedHeight,
  );

  const { error, data } = useQuery(GET_CLOSE_COLORS, {
    ...tokenOptions(token),
    variables: {
      take: perPage,
      skip: (currentPage - 1) * perPage,
      filter: filterExpression(filters, options?.clarities ?? []),
      okL: search.okL,
      okC: search.okC,
      okH: search.okH,
      patternCalibratedOnly: filters.patternOnly,
      fancyIntensity: search.fancyIntensity ?? null,
      fancySaturation: search.fancySaturation ?? null,
      fancyOvertone: search.fancyOvertone ?? null,
      fancyColor: search.fancyColor ?? null,
      langermanColor: search.langermanColor ?? null,
      flat: search.flat ?? null,
      melee: search.melee ?? null,
      excludeDiamondID: excludeDiamondID ?? null,
      webLikeOnly: picture === "weblike",
    },
  });

  useEffect(() => {
    if (data?.closestColors !== undefined) {
      setDisplayedData(data);
    }
  }, [data]);

  if (error) return <p>Error: {error.message}</p>;

  if (
    currentPage > 1 &&
    data?.closestColors?.items?.length === 0 &&
    data?.closestColors?.totalItems > 0
  ) {
    setCurrentPage(1);
  }

  const items = displayedData?.closestColors?.items;

  return (
    <PaginatedGallery
      width={computedWidth}
      height={computedHeight}
      cols={cols}
      rows={rows}
      page={currentPage}
      onPage={setCurrentPage}
      hasNext={(items?.length ?? 0) >= perPage}
      header={
        <Box>
          {colorArea(picture)}
          <SectionTitle>Filters</SectionTitle>
          {colorFilters && <Box sx={{ mb: 1 }}>{colorFilters}</Box>}
          <StoneFilterBar
            filters={filters}
            onChange={(next) => {
              setFilters(next);
              setCurrentPage(1);
            }}
            options={options}
          />
          {/* Which picture the results are shown as. The tabs sit over the grid because they choose
              how it is drawn: the order is the same in both, but the web-likes leave out the stones
              that have none, so a switch starts again from the first page. */}
          <Tabs
            value={picture === "original" ? 0 : 1}
            onChange={(_, value) => {
              setPicture(value === 0 ? "original" : "weblike");
              setCurrentPage(1);
            }}
            sx={{ borderBottom: 1, borderColor: "divider", mt: 1 }}
          >
            <Tab label="Original" />
            <Tab label="Web-like" />
          </Tabs>
        </Box>
      }
    >
      {(items ?? []).map((dist: any) => (
        <RouterLink
          to={"/analytics/fromdiamond/" + dist.diamondAnalysis.diamond?.id}
          target="_blank"
          rel="noopener"
          key={dist.diamondAnalysis.id}
        >
          <Tooltip
            title={<DiamondTooltip diamond={dist?.diamondAnalysis?.diamond} />}
          >
            <img
              width={128}
              height={128}
              style={{ display: "block", maxHeight: 128, objectFit: "contain" }}
              src={`${backendURL}/image/thumbnail/${pictureID(dist, picture)}`}
              alt={`Analysis ${dist.diamondAnalysis.id}`}
              loading="lazy"
            />
          </Tooltip>
        </RouterLink>
      ))}
    </PaginatedGallery>
  );
}

// A page that ranks the stock by color: the page's own color reference on top, then the filters
// both searches share, then the gallery. The Visual picker and Similar stone differ only in what
// they put in the color area — the query below it is the same one.
const StoneSearch = (props: StoneSearchProps) => (
  <SectionPaper
    sx={{
      height: `calc(100vh - ${GALLERY_CHROME}px)`,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}
  >
    <PageTitle />
    <Box sx={{ flex: 1, minHeight: 0 }}>
      <AutoSizer>
        {(size: Size) => (
          <StoneGallery
            {...props}
            computedWidth={size.width}
            computedHeight={size.height}
          />
        )}
      </AutoSizer>
    </Box>
  </SectionPaper>
);

export default StoneSearch;
